import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ITenant } from "../../views/dashboard/header/tenants/tenants.interface";

/**
 * Selected tenant information
 */
interface ISelectedTenant {
  id: string;
  name: string;
}

/**
 * Tenant data structure
 */
export interface ITenantData {
  selectedTenant: ISelectedTenant;
  tenants: ITenant[];
}

/**
 * Tenants state interface
 */
export interface TenantsState {
  tenantsData: ITenantData;
}

const initialState: TenantsState = {
  tenantsData: {
    selectedTenant: {
      id: "",
      name: "",
    },
    tenants: [],
  },
};

/**
 * Tenants slice for managing tenant and instance data
 *
 * This slice handles tenant selection, tenant list management,
 * and tenant-related operations across the application.
 *
 * @example
 * ```typescript
 * // Save complete tenants data
 * dispatch(saveTenantsList({
 *   selectedTenant: { id: '123', name: 'Acme Corp' },
 *   tenants: [...]
 * }));
 *
 * // Update only the tenants list
 * dispatch(updateTenantsList([...]));
 *
 * // Set selected tenant
 * dispatch(setSelectedTenant({ id: '123', name: 'Acme Corp' }));
 *
 * // Add a new tenant
 * dispatch(addTenant(newTenant));
 *
 * // Remove a tenant
 * dispatch(removeTenant('tenant-id'));
 * ```
 */
const tenantsSlice = createSlice({
  name: "tenants",
  initialState,
  reducers: {
    /**
     * Save complete tenants data including selected tenant and tenant list
     * @param state - Current tenants state
     * @param action - Payload containing complete tenant data
     */
    saveTenantsList: (state, action: PayloadAction<ITenantData>) => {
      state.tenantsData = action.payload;
    },

    /**
     * Update only the tenants list (keeping selected tenant unchanged)
     * @param state - Current tenants state
     * @param action - Payload containing updated tenants array
     */
    updateTenantsList: (state, action: PayloadAction<ITenant[]>) => {
      state.tenantsData.tenants = action.payload;
    },

    /**
     * Set the selected tenant
     * @param state - Current tenants state
     * @param action - Payload containing selected tenant info
     */
    setSelectedTenant: (state, action: PayloadAction<ISelectedTenant>) => {
      state.tenantsData.selectedTenant = action.payload;
    },

    /**
     * Add a new tenant to the list
     * @param state - Current tenants state
     * @param action - Payload containing the new tenant
     */
    addTenant: (state, action: PayloadAction<ITenant>) => {
      state.tenantsData.tenants.push(action.payload);
    },

    /**
     * Update an existing tenant in the list
     * @param state - Current tenants state
     * @param action - Payload containing tenant id and updated data
     */
    updateTenant: (state, action: PayloadAction<{ id: string; updates: Partial<ITenant> }>) => {
      const index = state.tenantsData.tenants.findIndex((t) => t.id === action.payload.id);
      if (index !== -1) {
        state.tenantsData.tenants[index] = {
          ...state.tenantsData.tenants[index],
          ...action.payload.updates,
        };
      }
    },

    /**
     * Remove a tenant from the list
     * @param state - Current tenants state
     * @param action - Payload containing tenant id to remove
     */
    removeTenant: (state, action: PayloadAction<string>) => {
      state.tenantsData.tenants = state.tenantsData.tenants.filter((t) => t.id !== action.payload);
    },

    /**
     * Clear all tenants data
     * @param state - Current tenants state
     */
    clearTenantsData: (state) => {
      state.tenantsData = {
        selectedTenant: { id: "", name: "" },
        tenants: [],
      };
    },
  },
});

export const { saveTenantsList, updateTenantsList, setSelectedTenant, clearTenantsData } =
  tenantsSlice.actions;

export default tenantsSlice.reducer;
